// React
import React, { Fragment, useEffect, useMemo, useState, useSyncExternalStore } from "react";

import { Close } from "@mui/icons-material";
// MUI
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";

// Hooks
import {
  formatHotkey,
  getShortcutsSnapshot,
  onOpenShortcutsHelp,
  registerShortcut,
  subscribeShortcuts,
} from "hooks/ui/hotkeys";
import useHotkeys from "hooks/ui/useHotkeys";

const Keys = ({ spec, label, thenLabel }) => {
  if (label) return <kbd className="kbd">{label}</kbd>;
  const chords = formatHotkey(spec);
  return chords.map((chord, chordIndex) => (
    <Fragment key={chordIndex}>
      {chordIndex > 0 && <span className="mx-1 text-xs text-gray-500">{thenLabel}</span>}
      <span className="inline-flex items-center gap-0.5">
        {chord.map((key, keyIndex) => (
          <kbd key={keyIndex} className="kbd">
            {key}
          </kbd>
        ))}
      </span>
    </Fragment>
  ));
};

/**
 * The "?" dialog: lists every shortcut registered with useHotkeys (bindings with a description)
 * or registerShortcut, grouped. Mounted once in App.jsx; open it from code with
 * openShortcutsHelp() (hooks/ui/useHotkeys).
 */
export default function ShortcutsHelp() {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const entries = useSyncExternalStore(
    subscribeShortcuts,
    getShortcutsSnapshot,
    getShortcutsSnapshot
  );
  const general = t("shortcuts.groups.general");

  useHotkeys({
    "?": {
      handler: () => setOpen(true),
      description: t("shortcuts.items.help"),
      group: general,
    },
  });

  useEffect(() => onOpenShortcutsHelp(() => setOpen(true)), []);

  // Shortcuts of the context menu, handled by ContextMenuProvider
  useEffect(() => {
    const unregister = [
      registerShortcut({
        keys: "shift+f10",
        description: t("shortcuts.items.contextMenuKey"),
        group: general,
      }),
      registerShortcut({
        keysLabel: t("keys.rightClick"),
        description: t("shortcuts.items.rightClick"),
        group: general,
      }),
      registerShortcut({
        keysLabel: t("keys.altRightClick"),
        description: t("shortcuts.items.nativeMenu"),
        group: general,
      }),
      registerShortcut({
        keys: "escape",
        description: t("shortcuts.items.escape"),
        group: general,
      }),
    ];
    return () => unregister.forEach((fn) => fn());
  }, [t, general]);

  const groups = useMemo(() => {
    const seen = new Set();
    const byGroup = new Map([[general, []]]);
    entries.forEach((entry) => {
      const id = `${entry.keysLabel || [].concat(entry.keys).join("|")}::${entry.description}`;
      if (seen.has(id)) return;
      seen.add(id);
      const group = entry.group || t("shortcuts.groups.page");
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group).push(entry);
    });
    return [...byGroup.entries()].filter(([, list]) => list.length);
  }, [entries, general, t]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="shortcuts-help-title"
    >
      <DialogTitle
        id="shortcuts-help-title"
        className="flex items-center justify-between gap-4"
        sx={{ fontSize: 18, fontWeight: 600, pr: 1.5 }}
      >
        {t("shortcuts.title")}
        <IconButton aria-label={t("shortcuts.close")} onClick={() => setOpen(false)} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {groups.length === 0 && <p className="text-sm text-gray-500">{t("shortcuts.empty")}</p>}
        {groups.map(([group, list]) => (
          <section key={group} className="mb-5 last:mb-1">
            <h3 className="section-title mb-2">{group}</h3>
            <ul className="divide-y divide-gray-100">
              {list.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-4 py-2 text-sm text-gray-700"
                >
                  <span>{entry.description}</span>
                  <span className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                    {entry.keysLabel ? (
                      <Keys label={entry.keysLabel} />
                    ) : (
                      [].concat(entry.keys).map((spec, index) => (
                        <Fragment key={spec}>
                          {index > 0 && <span className="text-xs text-gray-400">/</span>}
                          <Keys spec={spec} thenLabel={t("shortcuts.then")} />
                        </Fragment>
                      ))
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </DialogContent>
    </Dialog>
  );
}
